// Lib
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose, onlyUpdateForPropTypes } from '../../../../node_module_clones/recompose';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Components
import CurrentBoardHeaderStatus from './CurrentBoardHeaderStatus';
import HeaderShareButtonUtil from './sharing/HeaderShareButtonUtil';
import ViewUtil from './view/ViewUtil';
import ExportUtil from '../../export/ExportUtil';
import TemplateStatusUtil from './templateStatus/TemplateStatusUtil';
import MadeWithMilanoteUtil from './madeWithMilanote/MadeWithMilanoteUtil';
import CurrentBoardMenuUtil from './currentBoardMenu/CurrentBoardMenuUtil';
import SignUpToMilanoteButton from './signUpButton/SignUpToMilanoteButton';
import CurrentBoardHeaderUtil from './CurrentBoardHeaderUtil';

import BreadcrumbList from '../../breadcrumbs/Components/BreadcrumbList';
import CurrentBoardHeaderTitle from './CurrentBoardHeaderTitle';
import SaveToHomeButton, { SAVE_TO_HOME_BUTTON_WIDTH } from './SaveToHomeButton';
import ExportPopupForBoard from '../../export/ExportPopupForBoard';
import Measure from '../../../../node_module_clones/react-measure/src/Measure';

// Selectors
import { getElementFilterQuerySelector } from '../../../element/elementFilter/elementFilterSelector';
import {
    showCurrentBoardMenuSelector,
    showExportUtilSelector,
    showMadeWithMilanoteLinkSelector,
    showShareUtilSelector,
    showSignUpToMilanoteButtonSelector,
    showViewUtilSelector,
    showWriteStatusUtilSelector,
} from './currentBoardHeaderSelector';
import { currentBoardIsPublishedSelector } from '../../../utils/permissions/elementPermissionsSelector';
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';

// Util
import { isPlatformIframe } from '../../../platform/utils/platformDetailsUtils';
import { getIsTemplate } from '../../../../common/templates/templateUtil';

// Constants
import { PopupIds, PopupSections } from '../../../components/popupPanel/popupConstants';

// Styles
import './CurrentBoardHeader.scss';

const mapStateToProps = createStructuredSelector({
    filterQuery: getElementFilterQuerySelector,
    // Util toggles
    showShareUtil: showShareUtilSelector,
    showExportUtil: showExportUtilSelector,
    showViewUtil: showViewUtilSelector,
    showWriteStatusUtil: showWriteStatusUtilSelector,
    showMadeWithMilanoteLink: showMadeWithMilanoteLinkSelector,
    showSignUpToMilanoteButton: showSignUpToMilanoteButtonSelector,
    showCurrentBoardMenuUtil: showCurrentBoardMenuSelector,
    currentBoardIsPublished: currentBoardIsPublishedSelector,
    platformDetails: getPlatformDetailsSelector,
});

const HeaderLeft = ({
    permissions,
    breadcrumbObj,
    currentBoardId,
    rootBoardId,
    availableBreadcrumbsWidth,
    isLoadingBreadcrumbs,
    isPublishPreviewMode,
    isGuest,
    platformDetails,
    isSidebarProtypeEnabled,
}) => {
    const showDetachedBreadcrumbs =
        isPublishPreviewMode ||
        (!isLoadingBreadcrumbs && breadcrumbObj.detached && breadcrumbObj.complete) ||
        (isLoadingBreadcrumbs && breadcrumbObj.detached) ||
        isPlatformIframe(platformDetails);

    if (showDetachedBreadcrumbs) {
        const showSaveToHome =
            !isLoadingBreadcrumbs &&
            breadcrumbObj.detached &&
            breadcrumbObj.complete &&
            // If we've exceeded the maximum number of iterations for the breadcrumb then don't show the save button
            !breadcrumbObj.error &&
            !isGuest &&
            !isPlatformIframe(platformDetails);

        return (
            <>
                {!isSidebarProtypeEnabled && showSaveToHome && <SaveToHomeButton currentBoardId={currentBoardId} />}
                <BreadcrumbList
                    availableBreadcrumbsWidth={availableBreadcrumbsWidth}
                    breadcrumbObj={breadcrumbObj}
                    currentBoardId={currentBoardId}
                    rootBoardId={rootBoardId}
                />
            </>
        );
    }

    return <CurrentBoardHeaderStatus permissions={permissions} />;
};

HeaderLeft.propTypes = {
    isPublishPreviewMode: PropTypes.bool,
    showUploadProgress: PropTypes.bool,
    isLoadingBreadcrumbs: PropTypes.bool,
    currentBoardId: PropTypes.string,
    rootBoardId: PropTypes.string,
    permissions: PropTypes.number,
    availableBreadcrumbsWidth: PropTypes.number,
    breadcrumbObj: PropTypes.object,
    isGuest: PropTypes.bool,
    platformDetails: PropTypes.object,
    isSidebarProtypeEnabled: PropTypes.bool,
};

const CurrentBoardHeader = ({
    permissions,
    currentBoardId,
    rootBoardId,
    isCurrentBoardRoot,
    isGuest,
    isPublishPreviewMode,
    isCurrentBoardFetched,
    isEditable,
    filterQuery,
    currentBoard,
    breadcrumbObj,
    showUploadProgress,
    isLoadingBreadcrumbs,
    // Util toggles
    showShareUtil,
    showExportUtil,
    showViewUtil,
    showWriteStatusUtil,
    showMadeWithMilanoteLink,
    showSignUpToMilanoteButton,
    showCurrentBoardMenuUtil,
    currentBoardIsPublished,
    platformDetails,
    isSidebarProtypeEnabled,
}) => {
    const [titleWidth, setTitleWidth] = useState(0);
    const [headerWidth, setHeaderWidth] = useState(0);

    // 20 is padding
    const availableBreadcrumbsWidth = headerWidth / 2 - titleWidth / 2 - SAVE_TO_HOME_BUTTON_WIDTH - 20;

    const shouldMeasureWidth = breadcrumbObj && breadcrumbObj.detached;

    // Hide current board header on root
    if (isCurrentBoardRoot) return null;

    const isSingleRow = isGuest || isPublishPreviewMode || isPlatformIframe(platformDetails);

    const classes = classNames('CurrentBoardHeader', { 'single-header-row': isSingleRow });

    return (
        <Measure
            whitelist={['width']}
            onMeasure={({ width }) => setHeaderWidth(width)}
            shouldMeasure={shouldMeasureWidth}
        >
            <div className={classes}>
                <div className="header-left">
                    <HeaderLeft
                        isGuest={isGuest}
                        isPublishPreviewMode={isPublishPreviewMode}
                        showUploadProgress={showUploadProgress}
                        permissions={permissions}
                        breadcrumbObj={breadcrumbObj}
                        isLoadingBreadcrumbs={isLoadingBreadcrumbs}
                        currentBoardId={currentBoardId}
                        rootBoardId={rootBoardId}
                        availableBreadcrumbsWidth={availableBreadcrumbsWidth}
                        platformDetails={platformDetails}
                        isSidebarProtypeEnabled={isSidebarProtypeEnabled}
                    />
                </div>

                <div className="header-center">
                    <div className="spacer" />

                    <CurrentBoardHeaderTitle
                        filterQuery={filterQuery}
                        currentBoardId={currentBoardId}
                        isEditable={isEditable}
                        currentBoard={currentBoard}
                        setTitleWidth={setTitleWidth}
                        shouldMeasureWidth={shouldMeasureWidth}
                    />

                    <div className="title-right">
                        {getIsTemplate(currentBoard) ? <TemplateStatusUtil currentBoard={currentBoard} /> : null}
                    </div>
                </div>

                <div className="header-right">
                    {showShareUtil && <HeaderShareButtonUtil />}
                    {showExportUtil && (
                        <ExportUtil
                            popupId={PopupIds.EXPORT}
                            popupSection={PopupSections.HEADER}
                            WrapperComponent={CurrentBoardHeaderUtil}
                            exportPopupElement={<ExportPopupForBoard elementId={currentBoardId} />}
                        />
                    )}
                    {showViewUtil && <ViewUtil isCurrentBoardFetched={isCurrentBoardFetched} />}
                    {showMadeWithMilanoteLink && <MadeWithMilanoteUtil />}
                    {showSignUpToMilanoteButton && <SignUpToMilanoteButton />}
                    {showCurrentBoardMenuUtil && <CurrentBoardMenuUtil currentBoardId={currentBoardId} />}
                </div>
            </div>
        </Measure>
    );
};

CurrentBoardHeader.propTypes = {
    permissions: PropTypes.number,
    currentBoardId: PropTypes.string.isRequired,
    rootBoardId: PropTypes.string,
    isCurrentBoardRoot: PropTypes.bool,
    isEditable: PropTypes.bool,
    isCurrentBoardFetched: PropTypes.bool,
    isGuest: PropTypes.bool,
    isPublishPreviewMode: PropTypes.bool,
    isLoadingBreadcrumbs: PropTypes.bool,
    getDocumentElementSizes: PropTypes.func,
    currentBoard: PropTypes.object,
    breadcrumbObj: PropTypes.object,
    showUploadProgress: PropTypes.bool,
    filterQuery: PropTypes.string,
    currentBoardIsPublished: PropTypes.bool,
    platformDetails: PropTypes.object,
    isSidebarProtypeEnabled: PropTypes.bool,

    // Util toggles
    showShareUtil: PropTypes.bool,
    showExportUtil: PropTypes.bool,
    showViewUtil: PropTypes.bool,
    showWriteStatusUtil: PropTypes.bool,
    showMadeWithMilanoteLink: PropTypes.bool,
    showSignUpToMilanoteButton: PropTypes.bool,
    showCurrentBoardMenuUtil: PropTypes.bool,
};

const enhance = compose(connect(mapStateToProps), onlyUpdateForPropTypes);

export default enhance(CurrentBoardHeader);
